<template>
    <div>
        <KDialog v-show="showPopup" :title="'Change Workpool'" @close="cancel">
            <div class="k-mb-5">
                <p>Select the workpool to move the encounter to</p>
                <KDropDownList :data-items="workpools"
                               :text-field="'text'"
                               :data-item-key="'actionLink'"
                               :value="selectedWorkPool"
                               :default-item="{ text: 'Select Workpool', actionLink: null }"
                               :popup-settings="{ className: 'dropDownWidthFix'}"
                               @change="onWorkPoolSelectionChange">
                </KDropDownList>
            </div>
            <div class="k-mb-1">
                <p>Select the user to assign the encounter to</p>
                <KDropDownList :data-items="workPoolUsers"
                               :text-field="'text'"
                               :data-item-key="'actionLink'"
                               :value="selectedWorkPoolUser"
                               :default-item="{ text: 'Select User', actionLink: null }"
                               :popup-settings="{ className: 'dropDownWidthFix'}"
                               :disabled="!selectedWorkPool || selectedWorkPool.actionLink === 'select'"
                               @change="onUserSelectionChange">
                </KDropDownList>
            </div>
            <KDialogActionsbar layout="end">
                <KButton :theme-color="'primary'" @click='confirmChange'>Confirm</KButton>
                <KButton :theme-color="'info'" :fill-mode="'outline'" @click='cancel'>Cancel</KButton>
            </KDialogActionsbar>
        </KDialog>
    </div>
</template>

<script setup>
    import { defineProps, defineEmits, ref, watch } from 'vue';
    import { Dialog as KDialog, DialogActionsBar as KDialogActionsbar } from '@progress/kendo-vue-dialogs';
    import { Button as KButton } from '@progress/kendo-vue-buttons';
    import { DropDownList as KDropDownList } from "@progress/kendo-vue-dropdowns";
    import logger from "./../../modules/logger";
    import { fetchAPI } from '../../modules/fetchAPI';

    const props = defineProps(['encounterId', 'currentWorkpoolId', 'showDialog']);
    const emit = defineEmits(['moved', 'movecancelled']);

    const workpools = ref([]);
    const workPoolUsers = ref([]);

    const selectedWorkPool = ref(null);
    const selectedWorkPoolUser = ref(null);

    const showPopup = ref(false);

    watch(() => props.showDialog, (newValue) => { updated(newValue); });

    function updated(newValue) {
        showPopup.value = newValue;

        if (showPopup.value === true) {
            getWorkPools();
            resetWorkPoolUsers();
        }
    }

    function getWorkPools() {
        let request = {
            encounterId: props.encounterId,
            currentWorkpoolId: props.currentWorkpoolId
        }

        fetchAPI('api/Encounter/GetEncounterWorkpools', request)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                workpools.value = [...data];
                selectedWorkPool.value = null;
            })
            .catch(error => logger.error('Error: ' + error))
            .finally(() => {
            });
    
    }

    function getWorkPoolUsers() {
        if (!selectedWorkPool.value || !selectedWorkPool.value.actionLink) {
            resetWorkPoolUsers();
            return;
        }

        let request = {
            encounterId: props.encounterId,
            workPoolId: selectedWorkPool.value.actionLink
        }

        fetchAPI('api/Encounter/GetWorkPoolUserListForMoveEncounter', request)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                workPoolUsers.value = [...data];
                selectedWorkPoolUser.value = null;
            })
            .catch(error => logger.error('Error: ' + error))
            .finally(() => {
            });
    }

    function resetWorkPoolUsers() {
        workPoolUsers.value = [];
        selectedWorkPoolUser.value = null;
    }

    function onWorkPoolSelectionChange(e) {
        selectedWorkPool.value = e.value;
        getWorkPoolUsers();
    }

    function onUserSelectionChange(e) {
        selectedWorkPoolUser.value = e.value;
    }

    function toggleDialog() {
        showPopup.value = !showPopup.value;
    }

    function cancel() {
        toggleDialog();
        emit("movecancelled");
    }

    function confirmChange() {
        let request = {
            encounterId: props.encounterId,
            currentWorkpoolId: props.currentWorkpoolId,
            changedWorkpoolId: selectedWorkPool.value.actionLink,
            assignToUserId: selectedWorkPoolUser.value?.actionLink ?? null
        }

        fetchAPI('api/Encounter/MoveEncounter', request)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                toggleDialog();
                    emit("moved", { result: data });
            })
            .catch(error => logger.error('Error: ' + error))
            .finally(() => {
            });
    }

</script>
